import React, { useEffect, useState } from 'react';
import {
  Button,
  Card,
  Col,
  Divider,
  Input,
  message,
  Result,
  Row,
  Select,
  Switch,
} from 'antd';
import { CheckOutlined, CloseOutlined } from '@ant-design/icons';
import PropTypes from 'prop-types';
import { connect, useDispatch, useSelector } from 'react-redux';
import { Form } from '@ant-design/compatible';
import { getUserGroupsAction, saveUserAction } from '../../actions/UserActions';
import { history } from '../../utils/history';
import CustomAddNewPageContent from '../../components/Content/CustomAddNewPageContent';
import Authorized from '../../components/Authorization/Authorized';
import checkEnabledFeatures from '../../components/Authorization/Feature';
import { useTranslation } from 'react-i18next';

// import '@ant-design/compatible/assets/index.css';


const { TextArea } = Input;
const { Option } = Select;

const AddUser = ({ form }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const [finished, setFinished] = useState(false);
  const [confirmDirty, setConfirmDirty] = useState(false);

  const { userGroups, currentUser } = useSelector(store => ({
    userGroups: store.userReducer.userGroups,
    currentUser: store.userReducer.currentUser,
  }));

  useEffect(() => {
    dispatch(getUserGroupsAction());
  }, []);

  const handleConfirmBlur = e => {
    const { value } = e.target;
    setConfirmDirty(confirmDirty || !!value);
  };

  const compareToFirstPassword = (rule, value, callback) => {
    if (value && value !== form.getFieldValue('password')) {
      callback(t('users.passwordsInconsistent'));
    } else {
      callback();
    }
  };

  const validateToNextPassword = (rule, value, callback) => {
    if (value && confirmDirty) {
      form.validateFields(['confirm'], { force: true });
    }
    callback();
  };

  const handleSubmit = e => {
    e.preventDefault();
    form.validateFieldsAndScroll((err, values) => {
      if (!err) {

        const dataToSend = {
          ...values,
          notification: {
            item: {
              create: values.itemCreate ? values.itemCreate : false,
              update: values.itemUpdate ? values.itemUpdate : false,
              delete: values.itemDelete ? values.itemDelete : false,
            },
          },
        };
        delete dataToSend.itemCreate;
        delete dataToSend.itemUpdate;
        delete dataToSend.itemDelete;
        // eslint-disable-next-line react/destructuring-assignment
        dispatch(saveUserAction(dataToSend)).then(() => {
          message.success(t('users.userSaved'));
          form.resetFields();
          setFinished(true);
        });
      }
    });
  };

  const handleGoUsers = () => {
    history.push('/user-list');
  };

  const handleGoAddNew = () => {
    window.location.reload();
  };

  const formItemLayout = {
    labelCol: {
      xs: { span: 24 },
      sm: { span: 8 },
    },
    wrapperCol: {
      xs: { span: 24 },
      sm: { span: 16 },
    },
  };

  const { getFieldDecorator } = form;

  const itemNotifications = checkEnabledFeatures(
    currentUser,
    'Item-Cataloging',
  ) ? (
    <Card title={t('users.itemNotifications')} bordered={false}>
      <Row style={{ marginTop: 30 }} type="flex" justify="end">
        <Col span={5}>
          <Form.Item label={t('common.create')} wrapperCol={4} labelCol={4}>
            {getFieldDecorator('itemCreate', {
              valuePropName: 'checked',
              initialValue: false,
            })(
              <Switch
                checkedChildren={<CheckOutlined />}
                unCheckedChildren={<CloseOutlined />}
              />,
            )}
          </Form.Item>
        </Col>
        <Col span={5}>
          <Form.Item label={t('common.update')} wrapperCol={4} labelCol={4}>
            {getFieldDecorator('itemUpdate', {
              valuePropName: 'checked',
              initialValue: false,
            })(
              <Switch
                checkedChildren={<CheckOutlined />}
                unCheckedChildren={<CloseOutlined />}
              />,
            )}
          </Form.Item>
        </Col>
        <Col span={5}>
          <Form.Item label={t('common.delete')} wrapperCol={4} labelCol={4}>
            {getFieldDecorator('itemDelete', {
              valuePropName: 'checked',
              initialValue: false,
            })(
              <Switch
                checkedChildren={<CheckOutlined />}
                unCheckedChildren={<CloseOutlined />}
              />,
            )}
          </Form.Item>
        </Col>
      </Row>
    </Card>
  ) : (
    ''
  );

  return (
    <CustomAddNewPageContent title={t('users.addUser')} subTitle={t('users.userManagement')}>
      {!finished && (
        <Card bordered={false}>
          <Col span={12} offset={4}>
            {/* eslint-disable-next-line react/jsx-props-no-spreading */}
            <Form {...formItemLayout} onSubmit={handleSubmit}>
              <Card title={t('users.userDetails')} bordered={false}>
                <Form.Item label={t('common.name')}>
                  {getFieldDecorator('name', {
                    rules: [
                      {
                        required: true,
                        message: t('users.pleaseInputName'),
                      },
                    ],
                  })(<Input />)}
                </Form.Item>
                <Form.Item label={t('common.email')}>
                  {getFieldDecorator('email', {
                    rules: [
                      {
                        type: 'email',
                        message: t('users.invalidEmail'),
                      },
                      {
                        required: true,
                        message: t('users.pleaseInputEmail'),
                      },
                    ],
                  })(<Input />)}
                </Form.Item>
                <Form.Item label={t('common.username')}>
                  {getFieldDecorator('username', {
                    rules: [
                      {
                        required: true,
                        message: t('users.pleaseInputUsername'),
                      },
                    ],
                  })(<Input />)}
                </Form.Item>
                <Form.Item label={t('common.password')} hasFeedback>
                  {getFieldDecorator('password', {
                    rules: [
                      {
                        required: true,
                        message: t('users.pleaseInputPassword'),
                      },
                      {
                        validator: validateToNextPassword,
                      },
                    ],
                  })(<Input.Password />)}
                </Form.Item>
                <Form.Item label={t('common.confirmPassword')} hasFeedback>
                  {getFieldDecorator('confirm', {
                    rules: [
                      {
                        required: true,
                        message: t('users.pleaseConfirmPassword'),
                      },
                      {
                        validator: compareToFirstPassword,
                      },
                    ],
                  })(<Input.Password onBlur={handleConfirmBlur} />)}
                </Form.Item>
                <Form.Item label={t('common.userGroup')}>
                  {getFieldDecorator('userGroup', {
                    rules: [
                      {
                        required: true,
                        message: t('users.pleaseSelectUserGroup'),
                      },
                    ],
                  })(
                    <Select
                      allowClear
                      showSearch
                      filterOption
                      optionFilterProp="children"
                    >
                      {userGroups.map(item => (
                        <Option key={item.id} value={item.id}>
                          {item.value}
                        </Option>
                      ))}
                    </Select>,
                  )}
                </Form.Item>
                <Form.Item label={t('common.phoneNumber')}>
                  {getFieldDecorator('phoneNumber', {
                    rules: [],
                  })(<Input />)}
                </Form.Item>
                <Form.Item label={t('common.address')}>
                  {getFieldDecorator('address', {
                    rules: [],
                  })(<TextArea rows={3} />)}
                </Form.Item>
              </Card>
              {itemNotifications}
            </Form>
          </Col>
          <Authorized asset="user" operation="write">
            <Divider />
            <Row type="flex" justify="center">
              <Button type="primary" onClick={handleSubmit}>
                <CheckOutlined /> {t('common.save')}
              </Button>
            </Row>
          </Authorized>
        </Card>
      )}
      {finished && (
        <Result
          status="success"
          title={t('users.successfullyInserted')}
          extra={[
            <Button type="primary" key="console" onClick={handleGoUsers}>
              {t('users.goToUsers')}
            </Button>,
            <Button key="buy" onClick={handleGoAddNew}>
              {t('users.addUser')}
            </Button>,
          ]}
        />
      )}
    </CustomAddNewPageContent>
  );
};
AddUser.propTypes = {
  form: PropTypes.object,
  userGroups: PropTypes.array,
  getUserGroupsAction: PropTypes.func,
  currentUser: PropTypes.object,
  saveUserAction: PropTypes.func,
};

const WrappedAddUser = Form.create({ name: 'addUser' })(AddUser);

function mapStateToProps({ userReducer }) {
  const { userGroups } = userReducer;
  return { userGroups };
}
export default connect(mapStateToProps, {
  getUserGroupsAction,
  saveUserAction,
})(WrappedAddUser);
